import { FC, useEffect, useMemo, useRef, useState } from "react";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { cloneDeep } from "lodash";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowControlledTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import BeddingInputComponent from "../../CommonDataParts/BeddingInputComponent";
import { FormType } from "../../common";
import {
  BeefBreedingStageDataIntervention,
  BeefGrowingStageDataIntervention,
  BeefIntervention,
  BeefStageIntervention,
} from "../../../../models/Intervention/BeefIntervention";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import { enumToOptionsArrayWithTranslatedStrings } from "../../../../utils/obj-utils";
import { BeddingType } from "../../../../../../graphql/generated/blonk/cattle";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import EditModeButtons from "../../../helpers/EditModeButtons";
import {
  CattleBeddingSystem,
  CattleHousing,
} from "../../../../models/Intervention/CattleIntervention";
import { formatLabelName } from "../../../../utils/string-utils";

interface BeefBeddingSystemsProps {
  formType: FormType;
  stageIndex: number;
  baseline: BeefStageIntervention;
  isBeddingEdit: boolean;
  setIsBeddingEdit: (val: boolean) => void;
}

const BeefBeddingSystems: FC<BeefBeddingSystemsProps> = ({
  formType,
  stageIndex,
  baseline,
  isBeddingEdit,
  setIsBeddingEdit,
}) => {

  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const formContext = useFormContext<BeefIntervention>();
  const currResetValue = useRef<CattleHousing>();

  const [childKey, setChildKey] = useState(0);

  const beefStageData = baseline.stageData as
    | BeefGrowingStageDataIntervention
    | BeefBreedingStageDataIntervention;

  const beddingSystems = beefStageData.housing?.beddingSystems;

  const fieldItemPrefix = `stages.${stageIndex}.stageData.housing`;
  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const barnOutputMassUnit = userUOM?.unitBarnOutputMass
    ? unitLong(userUOM.unitBarnOutputMass)
    : "kg";

  const beddingAdditionsWatch = formContext.watch(
    `${fieldItemPrefix}.beddingSystemsAdditions`
  ) as CattleBeddingSystem[];

  useEffect(() => {
    if (isBeddingEdit) {
      currResetValue.current = cloneDeep(
        formContext.getValues(fieldItemPrefix)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBeddingEdit]);

  const resetEdit = () => {
    const beddingSystemsPrefix = `${fieldItemPrefix}.beddingSystems`;
    const baselineData =
      beefStageData?.housing?.beddingSystems?.filter(
        (item) => !!item.beddingType
      ) || [];

    baselineData?.forEach((item, index) => {
      Object.keys(item).forEach((key) => {
        const stringKey = String(key);
        const oldValue =
          currResetValue.current?.beddingSystems?.[index]?.[
          stringKey as keyof CattleBeddingSystem
          ];

        const path = `${beddingSystemsPrefix}[${index}].${key}`;
        formContext.setValue(path, oldValue ?? "");
      });
    });

    //reset additions

    const beddingAdditionsPrefix = `${fieldItemPrefix}.beddingSystemsAdditions`;
    formContext.setValue(beddingAdditionsPrefix, []);

    const beddingAdditions = currResetValue.current?.beddingSystemsAdditions;
    if (beddingAdditions?.length) {
      beddingAdditions?.forEach((item, index) => {
        Object.keys(item).forEach((key) => {
          const path = `${beddingAdditionsPrefix}[${index}].${key}`;
          formContext.setValue(path, item[key as keyof typeof item] ?? "");
        });
      });
    }

    setChildKey((prev) => prev + 1);
    setIsBeddingEdit(false);
  };

  const beddingOptions = useMemo(() => {
    const beddings = enumToOptionsArrayWithTranslatedStrings(
      BeddingType,
      intl,
      "SUSTELL.ENUMS.BEDDING_TYPE"
    );

    const defaultOption = {
      text: intl.formatMessage({
        id: "SUSTELL.ENUMS.BEDDING_TYPE.NO_BEDDING",
      }),
      value: "",
    };

    return [defaultOption, ...beddings];
  }, [intl]);

  return (
    <div>
      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({
          id: "SUSTELL.STAGE.POULTRY.HOUSING.BEDDING_TYPE",
        })}
      </p>
      <DsmGrid
        className={classes.dsmGridOneColumnWithBorder}
        style={{ padding: "var(--dsm-spacing-px-4)" }}
      >
        {beddingSystems
          ?.filter((item) => !!item.beddingType)
          ?.map((beddingItem, i: number) =>
            !isBeddingEdit &&
              !formContext.getValues(
                `${fieldItemPrefix}.beddingSystems[${i}].beddingAmount`
              ) ? (
              <DsmGrid className={classes.dsmGridThreeColumnTwoEqualLastSmall}>
                <ReactHookDsmInput
                  key={`${fieldItemPrefix}.beddingSystems[${i}].originalBeddingAmount`}
                  defaultValue={beddingItem.beddingAmount as string}
                  name={`${fieldItemPrefix}.beddingSystems[${i}].originalBeddingAmount`}
                  label={formatLabelName(beddingItem.beddingType ?? "")}
                  adornment={barnOutputMassUnit}
                  disabled
                  type="number"
                />
              </DsmGrid>
            ) : (
              <DsmGrid
                className={classes.dsmGridThreeColumnTwoEqualLastSmall}
                key={`${fieldItemPrefix}.beddingSystems[${i}].beddingType`}
                style={{
                  marginTop: i === 0 ? "0px" : "5px",
                  marginBottom: "16px",
                  height: "60px",
                }}
              >
                <input
                  type="hidden"
                  value={beddingItem.beddingType}
                  ref={formContext.register()}
                  name={`${fieldItemPrefix}.beddingSystems[${i}].beddingType`}
                />
                <RowControlledTextFieldWithMetrics
                  name={`${fieldItemPrefix}.beddingSystems[${i}].beddingAmount`}
                  label={beddingItem.beddingType?.replace("_", " ") as string}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.DAIRY.BEDDING.AMOUNT.TOOLTIP",
                  })}
                  metricUnit={barnOutputMassUnit}
                  baseline={beddingItem.beddingAmount}
                  disabled={formType === "view" || !isBeddingEdit}
                  minAllowedValue={0}
                  originalMetricUnit={barnOutputMassUnit}
                  displayTypeSelect={isBeddingEdit}
                />
              </DsmGrid>
            )
          )}

        {(isBeddingEdit ||
          (!isBeddingEdit && beddingAdditionsWatch?.length !== 0)) && (
            <div
              style={{
                marginTop: "16px",
              }}
            >
              <BeddingInputComponent
                stageIndex={stageIndex}
                beddingOptions={beddingOptions}
                isViewMode={formType === FormType.View || !isBeddingEdit}
                beddingAmoutWeightUnit={barnOutputMassUnit}
                beddingAmountTooltipId="SUSTELL.STAGE.DAIRY.BEDDING.AMOUNT.TOOLTIP"
                isIntervention
                key={childKey}
              />
            </div>
          )}
        {formType !== FormType.View &&
          <EditModeButtons
            isEditing={isBeddingEdit}
            onEdit={() => setIsBeddingEdit(true)}
            onCancel={resetEdit}
          />}
      </DsmGrid>
    </div>
  );
};

export default BeefBeddingSystems;
